import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import deleteIcon from "../../assets/icons/deleteRole.svg";
import DOWNLOAD_ICON from "../../assets/icons/download-master.svg";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaRegCopy,
} from "react-icons/fa";
import { ThemeContext } from "../../contexts/themeContext";
import { DataGrid } from "../../common";
import "../../assets/styles/card-blacklist.scss";
import { APIEndpoint } from "../../utils/axios";

const CopyableTruncatedCell = ({
  text,
  tooltipText,
  maxLength = 50,
  className = "",
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const displayText =
    text && text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text || "";

  return (
    <div className={`copyable-truncated-cell ${className}`}>
      <button
        type="button"
        className={`copy-btn ${copied ? "copied" : ""}`}
        onClick={handleCopy}
        title={copied ? "Copied!" : "Copy to clipboard"}
      >
        {copied ? (
          <FaCheckCircle size={11} color="#28a745" />
        ) : (
          <FaRegCopy size={11} />
        )}
      </button>
      <span className="cell-text" title={tooltipText || text}>
        {displayText}
      </span>
    </div>
  );
};

export default function CardBlacklist() {
  const { themedColor } = useContext(ThemeContext);
  const { t } = useTranslation();

  const [cards, setCards] = useState([]);

  // State controls
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  // Add Card inputs
  const [cardNumberInput, setCardNumberInput] = useState("");
  const [blacklistReason, setBlacklistReason] = useState(
    "Fraudulent transactions",
  );
  const [customReason, setCustomReason] = useState("");

  // Remove Card input
  const [removeReason, setRemoveReason] = useState("");

  const fetchCards = async () => {
    try {
      const res = await APIEndpoint.get("payments/card/blacklist");
      const rawItems =
        res?.items || res?.data?.items || (Array.isArray(res) ? res : []);
      if (rawItems) {
        const formatted = rawItems.map((it, idx) => ({
          id: it.id || it.key || `card_${idx}`,
          key: it.key || it.id || `key_${idx}`,
          masked:
            it.masked || it.cardNo || it.cardNumber || "XXXX XXXX XXXX XXXX",
          cardHash: it.cardHash || it.hash || it.card_hash || it.key || "",
          hash: it.hash || it.cardHash || it.card_hash || it.key || "",
          reason:
            it.reason || (it.meta ? it.meta.reason : "No reason provided"),
          blacklistedAt:
            it.blacklistedAt ||
            (it.meta
              ? it.meta.blacklistedAt || it.meta.timestamp || it.meta.createdAt
              : "") ||
            it.createdAt ||
            it.timestamp ||
            "",
          blacklistedBy:
            it.blacklistedBy ||
            (it.meta
              ? it.meta.operator || it.meta.blacklistedBy || it.meta.user
              : "system") ||
            "system",
        }));
        setCards(formatted);
      }
    } catch (err) {
      console.error("Error fetching blacklisted cards:", err);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const handleBlacklistSubmit = async (e) => {
    e.preventDefault();

    const finalReason =
      blacklistReason === "Other" ? customReason : blacklistReason;
    if (!finalReason.trim()) {
      alert("Please specify a reason for blacklisting.");
      return;
    }

    try {
      const res = await APIEndpoint.post("payments/card/blacklist/add", {
        cardHash: cardNumberInput,
        reason: finalReason,
      });

      if (res && (res.success || res.data?.success)) {
        setCardNumberInput("");
        setCustomReason("");
        setBlacklistReason("Fraudulent transactions");
        setShowAddModal(false);
        fetchCards();
      } else {
        alert(
          res?.message || res?.data?.message || "Failed to blacklist card.",
        );
      }
    } catch (err) {
      console.error(err);
      alert(
        err.response?.data?.message ||
          "Error occurred while blacklisting card.",
      );
    }
  };

  const handleRemoveConfirm = async () => {
    if (!showDeleteModal) return;

    const targetCard = showDeleteModal;
    const finalRemoveReason = removeReason.trim() || "Removed by Super Admin";

    try {
      const res = await APIEndpoint.post("payments/card/blacklist/remove", {
        key: targetCard.key,
        reason: finalRemoveReason,
      });

      if (res && (res.success || res.data?.success)) {
        setShowDeleteModal(null);
        setRemoveReason("");
        fetchCards();
      } else {
        alert(
          res?.message ||
            res?.data?.message ||
            "Failed to remove card from blacklist.",
        );
      }
    } catch (err) {
      console.error(err);
      alert(
        err.response?.data?.message || "Error occurred while removing card.",
      );
    }
  };

  // Hash column completely added to Columns list
  const Columns = [
    {
      field: "blacklistedAt",
      headerName: "Timestamp",
      flex: 1,
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleString() : "",
    },
    {
      field: "masked",
      headerName: "Card Number (Masked)",
      flex: 1.5,
      cellRenderer: (params) => {
        return (
          <div className="card-display">
            <span className="card-numbers">{params.data.masked}</span>
          </div>
        );
      },
    },
    {
      field: "cardHash",
      headerName: "Card Hash",
      flex: 1.5,
      cellRenderer: (params) => {
        return (
          <CopyableTruncatedCell
            text={params.data?.cardHash || ""}
            tooltipText={params.data?.cardHash || ""}
            maxLength={20}
          />
        );
      },
    },

    { field: "blacklistedBy", headerName: "Blacklisted By", flex: 1.2 },
    {
      field: "reason",
      headerName: "Reason",
      flex: 2,
      cellRenderer: (params) => {
        return (
          <CopyableTruncatedCell
            text={params.data?.reason || ""}
            tooltipText={params.data?.reason || ""}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      isRowPinned: true,
      flex: 0.5,
      cellRenderer: (params) => {
        return (
          <div className="icon-aircraft-container">
            <img
              className="aircraft-actions"
              alt="delete"
              onClick={(e) => setShowDeleteModal(params.data)}
              src={deleteIcon}
            />
            <span className="tooltip-action">Delete</span>
          </div>
        );
      },
    },
  ];

  const handleExportCSV = () => {
    if (!cards || cards.length === 0) {
      alert("No data available to export.");
      return;
    }

    const headers = [
      "Card Number (Masked)",
      "Card Hash",
      "Blacklisted At",
      "Blacklisted By",
      "Reason",
    ];

    const rows = cards.map((c) => [
      c.masked || "",
      c.cardHash || c.hash || "",
      c.blacklistedAt ? new Date(c.blacklistedAt).toLocaleString() : "",
      c.blacklistedBy || "",
      c.reason || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `card_blacklist_${new Date().toISOString().slice(0, 10)}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const ExportButton = () => (
    <>
      <button
        className="margRight"
        style={{ cursor: "pointer" }}
        title={" Export CSV"}
        onClick={handleExportCSV}
      >
        <img src={DOWNLOAD_ICON} className="node-icon svg" alt="" />
      </button>
    </>
  );

  const isDark = themedColor?.sidebar === "#181818";

  return (
    <div className={`blacklist-container ${isDark ? "dark" : "light"}`}>
      <div
        className="flexCol full-flex ag-theme-alpine-dark"
        style={{ marginTop: "10px" }}
      >
        <DataGrid
          className="full-flex"
          columns={Columns}
          showDefaultFilters={true}
          enableGridSearch={true}
          RightComponent={ExportButton}
          addButton={{
            title: t("pages.adminSub.blacklistNewCard"),
            onClick: () => setShowAddModal(true),
          }}
          
          rowModelType="clientSide"
          rowData={cards}
          columnResize={true}
          pagination={true}
          paginationPageSize={10}
          suppressPaginationPanel={false}
        />
      </div>

      {/* Add Card Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-premium">
            <div className="modal-header-premium">
              <h2>Blacklist New Card</h2>
              <button
                className="close-btn"
                onClick={() => setShowAddModal(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleBlacklistSubmit}>
              <div className="modal-body-premium">
                <div className="form-group-premium">
                  <label>Card Hash</label>
                  <input
                    type="text"
                    placeholder="e.g. 9287jks738hy878hgs"
                    value={cardNumberInput}
                    onChange={(e) => setCardNumberInput(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group-premium">
                  <label>Blacklist Reason Category</label>
                  <select
                    value={blacklistReason}
                    onChange={(e) => setBlacklistReason(e.target.value)}
                  >
                    <option value="Fraudulent transactions">
                      Multiple chargebacks / Fraudulent transactions
                    </option>
                    <option value="Stolen card alert">
                      Stolen card alert from banking networks
                    </option>
                    <option value="High-frequency testing activity">
                      High-frequency terminal testing velocity breach
                    </option>
                    <option value="Other">
                      Other Reason (Provide Details)
                    </option>
                  </select>
                </div>

                {blacklistReason === "Other" && (
                  <div className="form-group-premium">
                    <label>Provide Detailed Justification</label>
                    <textarea
                      rows="3"
                      placeholder="State exact reasons for adding this credential to blacklist..."
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      required
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer-premium">
                <button
                  type="button"
                  className="btn-premium btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-premium btn-primary">
                  Confirm Blacklist
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Card Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-premium">
            <div className="modal-header-premium">
              <h2>Confirm Removal</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowDeleteModal(null);
                  setRemoveReason("");
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body-premium">
              <div className="flexRow gap3 alignCenter">
                <FaExclamationTriangle className="text-danger" size={30} />
                <p className="p-2">
                  Are you sure you want to remove this card from the blacklist?
                </p>
              </div>

              <div className="delete-card-summary">
                <div className="summary-label">CARD DIGITS (MASKED)</div>
                <div className="Summary-masked">{showDeleteModal.masked}</div>
                <div className="summary-label">SHA-256 HASH</div>
                <div className="Summary-hash">{showDeleteModal.hash}</div>
              </div>

              <div className="form-group-premium" style={{ marginTop: "12px" }}>
                <label>Removal Reason (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Card re-verified by issuer, false positive…"
                  value={removeReason}
                  onChange={(e) => setRemoveReason(e.target.value)}
                  maxLength={200}
                />
              </div>
            </div>
            <div className="modal-footer-premium">
              <button
                type="button"
                className="btn-premium btn-secondary"
                onClick={() => {
                  setShowDeleteModal(null);
                  setRemoveReason("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-premium btn-danger"
                onClick={handleRemoveConfirm}
              >
                Confirm Removal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
